/**
 * The DOM side of marker names (`markerLabels.ts` holds the rules): every HTML marker with a name registers its
 * content element here; after a draw or a zoom the names are measured and the ones that collide are hidden, the
 * ones at the map's edge turned to the other side (`layoutLabels`). A hidden name keeps its place in the page
 * (`visibility`), so it is measured again and comes back when the map is closer.
 */
import { layoutLabels, type Box, type LabelItem } from './markerLabels';

export const LABEL_CLASS = 'agent-marker-label';
const HIDDEN = `${LABEL_CLASS}--hidden`;
const LEFT = `${LABEL_CLASS}--left`;

/** What the layout needs of an element — a DOM node, or a fake one in a test. */
export type LabelledElement = {
  isConnected: boolean;
  getBoundingClientRect(): Box;
  querySelector(selector: string): LabelNode | null;
};
export type LabelNode = {
  offsetWidth: number;
  offsetHeight: number;
  classList: { add(name: string): void; remove(name: string): void };
};

/** Measure and place the names of *entries* (marker element → rank) inside *container*. */
export function applyLabelLayout(container: { getBoundingClientRect(): Box }, entries: Iterable<[LabelledElement, number]>): void {
  const labelled = [...entries].filter(([element]) => element.isConnected)
    .map(([element, rank]) => ({ element, rank, label: element.querySelector(`.${LABEL_CLASS}`) }))
    .filter((entry): entry is typeof entry & { label: LabelNode } => !!entry.label);
  // every name to its natural place first: the right of its disc, shown
  for (const { label } of labelled) { label.classList.remove(HIDDEN); label.classList.remove(LEFT); }
  const bounds = container.getBoundingClientRect();
  const centre = { x: (bounds.left + bounds.right) / 2, y: (bounds.top + bounds.bottom) / 2 };
  const items: LabelItem[] = labelled.map(({ element, rank, label }) => {
    const disc = element.getBoundingClientRect();
    return { disc, width: label.offsetWidth, height: label.offsetHeight, rank,
      distance: Math.hypot((disc.left + disc.right) / 2 - centre.x, (disc.top + disc.bottom) / 2 - centre.y) };
  });
  layoutLabels(items, bounds).forEach((place, index) => {
    const { label } = labelled[index]!;
    if (!place.visible) label.classList.add(HIDDEN);
    else if (place.side === 'left') label.classList.add(LEFT);
  });
}

export type LabelLayout = {
  /** A marker's content element and its priority (lower wins). */
  add(element: HTMLElement, rank: number): void;
  remove(element: HTMLElement): void;
  /** Re-lay the names out on the next frame (calls in one frame make one pass). */
  refresh(): void;
  dispose(): void;
};

export function createLabelLayout(container: HTMLElement): LabelLayout {
  const entries = new Map<HTMLElement, number>();
  let frame = 0;
  let alive = true;
  const run = () => { frame = 0; if (alive) applyLabelLayout(container, entries); };
  const refresh = () => { if (alive && !frame) frame = requestAnimationFrame(run); };
  return {
    add(element, rank) { entries.set(element, rank); refresh(); },
    remove(element) { entries.delete(element); },
    refresh,
    dispose() {
      alive = false;
      if (frame) cancelAnimationFrame(frame);
      entries.clear();
    },
  };
}
